'use client'
import HotelImage from '@/src/components/common/hotels/hotelImage'
import { ROOM_CAROUSEL_MAX_VISIBLE } from '@/src/constants/hotels'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { useRef, useState } from 'react'

export interface RoomImageCarouselProps {
    images: string[]
    alt: string
    /** "{count} Photos" heading for the full-gallery dialog. */
    photosCountLabel: (count: number) => string
}

// Horizontal scroll-snap strip of room images with dot indicators and a "+N" badge that
// opens a full-screen grid dialog. UI-only state; safe in the presentation layer.
const RoomImageCarousel: React.FC<RoomImageCarouselProps> = ({ images, alt, photosCountLabel }) => {
    const [open, setOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const scrollRef = useRef<HTMLDivElement>(null)
    if (images.length === 0) return null

    const visible = images.slice(0, ROOM_CAROUSEL_MAX_VISIBLE)
    const remaining = images.length - visible.length

    // Track the snapped image so the dot indicators reflect the active slide (abs handles RTL).
    const onScroll = () => {
        const el = scrollRef.current
        if (!el) return
        const width = el.clientWidth || 1
        setActiveIndex(Math.round(Math.abs(el.scrollLeft) / width))
    }

    return (
        <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden' }}>
            <Box
                ref={scrollRef}
                onScroll={onScroll}
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none'
                }}
            >
                {visible.map((src, index) => (
                    <HotelImage
                        key={`${src}-${index}`}
                        src={src}
                        alt={`${alt} ${index + 1}`}
                        onClick={() => setOpen(true)}
                        wrapperSx={{
                            flex: '0 0 100%',
                            width: '100%',
                            height: { xs: 200, md: 340 },
                            scrollSnapAlign: 'start'
                        }}
                    />
                ))}
            </Box>

            {visible.length > 1 && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        insetInlineStart: 0,
                        insetInlineEnd: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '6px',
                        pointerEvents: 'none'
                    }}
                >
                    {visible.map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: index === activeIndex ? 8 : 7,
                                height: index === activeIndex ? 8 : 7,
                                borderRadius: '50%',
                                bgcolor: index === activeIndex ? 'neutral.white' : 'rgba(255,255,255,0.5)'
                            }}
                        />
                    ))}
                </Box>
            )}

            {remaining > 0 && (
                <Box
                    onClick={() => setOpen(true)}
                    role="button"
                    aria-label={`View ${remaining} more photos`}
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        insetInlineEnd: 12,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        border: 2,
                        borderColor: 'neutral.white',
                        color: 'neutral.white',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        textAlign: 'center',
                        cursor: 'pointer'
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'neutral.white', lineHeight: 1.1 }}>
                        +{remaining}
                    </Typography>
                </Box>
            )}

            <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
                <Box sx={{ bgcolor: 'neutral.white', minHeight: '100dvh' }}>
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: 'neutral.white',
                            borderBottom: 1,
                            borderColor: 'borders.default',
                            px: 2,
                            py: 1.5,
                            zIndex: 1
                        }}
                    >
                        <IconButton onClick={() => setOpen(false)} aria-label="Close gallery">
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'textColors.primary' }}>
                            {photosCountLabel(images.length)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 1,
                            p: 1
                        }}
                    >
                        {images.map((src, index) => (
                            <HotelImage
                                key={`${src}-${index}`}
                                src={src}
                                alt={`${alt} ${index + 1}`}
                                wrapperSx={{ borderRadius: 1, height: { xs: 240, md: 320 } }}
                            />
                        ))}
                    </Box>
                </Box>
            </Dialog>
        </Box>
    )
}

export { RoomImageCarousel }
export default RoomImageCarousel
